/**
 * Deterministic outcome-hue mapping — the single source of truth shared by the
 * web frontend and the mobile app.
 *
 * Maps an outcome to one of 16 OKLCh palette hues. This module returns ONLY hue
 * NAMES — it has no knowledge of CSS variables, hex values, or Tailwind classes.
 * Each platform owns its own hue → presentation mapping:
 *   - web: `useOutcomeColor` (template-literal Tailwind classes + CSS-var bridges)
 *   - mobile: `OUTCOME_HUE_CLASSES` (literal className map — Uniwind needs literals)
 *   - OG images: a hex table (Satori has no CSS cascade)
 *
 * Two modes are supported:
 *
 * - `'precise-order'` (DEFAULT) — canonical-index-based. A `marketPubkey`
 *   selects one of twelve curated palette orders using a stable FNV-1a hash.
 *   Anonymous design previews retain the original order described below,
 *   with the green/red valence
 *   anchors kept off positions 0/1. A Yes-derivative label always renders green
 *   and a No-derivative red (so binary Yes/No markets read conventionally), but
 *   green/red sit at positions 2/3 of `PRECISE_ORDER_HUES`, so a 2-outcome
 *   non-Yes/No market gets blue/purple — while a 3+-outcome market can still
 *   pick green/red up at a later position. The sequence is hand-tuned around
 *   four perceptual families (W/G/C/P — see `OUTCOME_HUE_FAMILY`) so adjacent
 *   positions are always in different families.
 *
 * - `'hash'` (legacy, kept for A/B + rollback) — title-derived: `"yes"` →
 *   `'green'`, `"no"` → `'red'`, else FNV-1a hash modulo 14 over `NON_RESERVED_HUES`.
 *
 * Distinct-index hue uniqueness holds in both modes for up to 16 outcomes; a
 * repeated index resolves to that outcome's single hue, and only markets with
 * > 16 DISTINCT indexes throw.
 *
 * Platform-neutral: pure string/array/regex/Math math only (no web- or RN-only
 * APIs, no `Array.prototype.toSorted` — Hermes-safe).
 */

import { marketOutcomePalette } from './market-outcome-palette';

export type OutcomeHueName =
  | 'red'
  | 'orange'
  | 'amber'
  | 'yellow'
  | 'lime'
  | 'green'
  | 'emerald'
  | 'teal'
  | 'cyan'
  | 'sky'
  | 'blue'
  | 'indigo'
  | 'violet'
  | 'purple'
  | 'fuchsia'
  | 'pink';

export type OutcomeRole = 'outline' | 'fill' | 'text' | 'cta' | 'cta-foreground';

/**
 * The 16 outcome hues in canonical order. Iterating this list (e.g. for a
 * swatch grid) must preserve the order — hue rows in palette tables match it.
 */
export const OUTCOME_HUES: readonly OutcomeHueName[] = [
  'red',
  'orange',
  'amber',
  'yellow',
  'lime',
  'green',
  'emerald',
  'teal',
  'cyan',
  'sky',
  'blue',
  'indigo',
  'violet',
  'purple',
  'fuchsia',
  'pink',
];

/** The 11 OKLCh steps from light to dark. */
export const OUTCOME_STEPS: readonly [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] = [
  50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950,
];

/** Numeric OKLCh step — use with `outcomeHueToStepVar` for data-viz strokes. */
export type OutcomePaletteStep = (typeof OUTCOME_STEPS)[number];

/**
 * The 14 hues available for non-reserved titles. Excludes `green` (Yes) and
 * `red` (No), in canonical order.
 */
const NON_RESERVED_HUES: readonly OutcomeHueName[] = [
  'orange',
  'amber',
  'yellow',
  'lime',
  'emerald',
  'teal',
  'cyan',
  'sky',
  'blue',
  'indigo',
  'violet',
  'purple',
  'fuchsia',
  'pink',
];

/**
 * Selection strategy for outcome hue assignment.
 *
 * - `'precise-order'` (DEFAULT) — canonical index into the market's seeded
 *   palette, or `PRECISE_ORDER_HUES` for anonymous previews.
 * - `'hash'` (legacy) — title-based FNV-1a + Yes/No reservation + collision walk.
 */
export type OutcomeColorMode = 'hash' | 'precise-order';

/**
 * Perceptual family of each of the 16 OUTCOME_HUES, derived from its OKLCh hue
 * angle: W = warms (red..yellow), G = greens (lime..teal), C = cools
 * (cyan..indigo), P = purples/pinks (violet..pink). The `PRECISE_ORDER_HUES`
 * sequence guarantees every adjacent position pair is in different families.
 */
export const OUTCOME_HUE_FAMILY: Readonly<Record<OutcomeHueName, 'W' | 'G' | 'C' | 'P'>> = {
  red: 'W',
  orange: 'W',
  amber: 'W',
  yellow: 'W',
  lime: 'G',
  green: 'G',
  emerald: 'G',
  teal: 'G',
  cyan: 'C',
  sky: 'C',
  blue: 'C',
  indigo: 'C',
  violet: 'P',
  purple: 'P',
  fuchsia: 'P',
  pink: 'P',
};

/**
 * Deterministic 16-position permutation of `OUTCOME_HUES` used by precise-order
 * mode. Hand-tuned:
 * 1. Positions 0/1 are NON-valence hues (blue, purple) so a 2-outcome market
 *    never defaults to green/red; the green/red anchors sit at positions 2/3.
 * 2. Adjacent positions are always in different perceptual families.
 * 3. Tight perceptual clusters have their second member pushed as late as
 *    possible (no two of {green, emerald, lime} co-occur until > 10 outcomes).
 */
export const PRECISE_ORDER_HUES: readonly OutcomeHueName[] = [
  'blue',
  'purple',
  'green',
  'red',
  'teal',
  'yellow',
  'cyan',
  'pink',
  'sky',
  'violet',
  'indigo',
  'lime',
  'orange',
  'fuchsia',
  'emerald',
  'amber',
];

// Module-load invariants: a true 16-permutation of OUTCOME_HUES, green/red kept
// OUT of positions 0/1, no two adjacent positions sharing a perceptual family.
// Defensive guards against future edits that silently break the contrast contract.
if (PRECISE_ORDER_HUES.length !== OUTCOME_HUES.length) {
  throw new Error(
    `PRECISE_ORDER_HUES: expected length ${String(OUTCOME_HUES.length)}, got ${String(PRECISE_ORDER_HUES.length)}`,
  );
}
if (new Set(PRECISE_ORDER_HUES).size !== OUTCOME_HUES.length) {
  throw new Error('PRECISE_ORDER_HUES: not a 16-permutation (duplicate hue detected)');
}
if (
  PRECISE_ORDER_HUES[0] === 'green' ||
  PRECISE_ORDER_HUES[0] === 'red' ||
  PRECISE_ORDER_HUES[1] === 'green' ||
  PRECISE_ORDER_HUES[1] === 'red'
) {
  throw new Error('PRECISE_ORDER_HUES: green/red must NOT occupy positions 0/1');
}
for (let i = 0; i < PRECISE_ORDER_HUES.length - 1; i++) {
  const a = PRECISE_ORDER_HUES[i];
  const b = PRECISE_ORDER_HUES[i + 1];
  if (a === undefined || b === undefined) continue;
  if (OUTCOME_HUE_FAMILY[a] === OUTCOME_HUE_FAMILY[b]) {
    throw new Error(
      `PRECISE_ORDER_HUES: adjacent positions ${String(i)}/${String(i + 1)} share family ${OUTCOME_HUE_FAMILY[a]}`,
    );
  }
}

/**
 * Yes-derivative label test — `true` for `"yes"` and natural derivatives like
 * `"Yes — ban them"` (a leading `yes` token NOT followed by another letter).
 * `"Yesterday"` is NOT a match. Case-insensitive, trimmed.
 */
export function isYesOutcomeTitle(title: string): boolean {
  return /^yes(?![a-z])/i.test(title.trim());
}

/**
 * No-derivative label test — `true` for `"no"` and derivatives like
 * `"No — let people play"`. `"Norway"` / `"None"` are NOT matches.
 */
export function isNoOutcomeTitle(title: string): boolean {
  return /^no(?![a-z])/i.test(title.trim());
}

/**
 * FNV-1a hash — good distribution for short strings. Returns a positive 32-bit
 * integer.
 */
function fnv1a(str: string): number {
  let hash = 0x811c9dc5; // FNV offset basis
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193); // FNV prime
  }
  return hash >>> 0; // Ensure unsigned 32-bit
}

/**
 * Hash-mode: deterministic mapping of a title to one of the 16 hue names.
 * `'yes'` → `'green'`, `'no'` → `'red'`, else FNV-1a modulo 14 over
 * `NON_RESERVED_HUES`. Pure; never throws.
 */
function outcomeHueFromTitleHash(title: string): OutcomeHueName {
  const normalized = title.trim().toLowerCase();
  if (normalized === 'yes') return 'green';
  if (normalized === 'no') return 'red';
  const hash = fnv1a(title);
  const index = hash % NON_RESERVED_HUES.length;
  const hue = NON_RESERVED_HUES[index];
  if (hue === undefined) {
    throw new Error(`outcomeHueFromTitle: hue index ${String(index)} out of range`);
  }
  return hue;
}

/**
 * Precise-order single-outcome resolver. A Yes-derivative label always wins
 * `green` and a No-derivative wins `red`, regardless of position. Every other
 * outcome takes its canonical index's hue from the chosen palette. Throws if `index`
 * is out of range.
 */
function outcomeHueFromIndexPreciseOrder(
  title: string,
  index: number,
  marketPubkey?: string,
): OutcomeHueName {
  if (isYesOutcomeTitle(title)) return 'green';
  if (isNoOutcomeTitle(title)) return 'red';
  const hue = preciseOrderPalette(marketPubkey)[index];
  if (hue === undefined) {
    throw new Error(`outcomeHueFromTitle: precise-order index ${String(index)} out of range`);
  }
  return hue;
}

/**
 * Mode-aware single-outcome hue resolver.
 *
 * - `'precise-order'` (default): `index` is REQUIRED and `title` only drives the
 *   Yes/No valence override. Throws if `index` is undefined.
 * - `'hash'`: `title` drives the result; `index` is ignored.
 *
 * Pure: same inputs always return the same output.
 */
export function outcomeHueFromTitle(
  title: string,
  index?: number,
  mode: OutcomeColorMode = 'precise-order',
  marketPubkey?: string,
): OutcomeHueName {
  if (mode === 'precise-order') {
    if (index === undefined) {
      throw new Error('outcomeHueFromTitle: index is required in precise-order mode');
    }
    return outcomeHueFromIndexPreciseOrder(title, index, marketPubkey);
  }
  return outcomeHueFromTitleHash(title);
}

/**
 * Precise-order hue from a bare outcome index (no title/valence). Negative or
 * out-of-range indices wrap via modulo — defensive; the contract caps at 16.
 * Prefer `outcomeHuesForMarket` when colouring a whole market's outcomes
 * together (it applies Yes/No valence + collision avoidance).
 */
export function hueFromOutcomeIndex(index: number, marketPubkey?: string): OutcomeHueName {
  const palette = preciseOrderPalette(marketPubkey);
  const len = palette.length;
  // JS `%` yields a negative remainder for negative operands; normalise.
  const i = ((index % len) + len) % len;
  return palette[i] ?? 'green';
}

/**
 * Hash-mode strict-unique per-market hue assignment. Yes/No reservation pre-pass
 * + FNV-1a + collision-walk through `OUTCOME_HUES` in `index` order. Returns hues
 * in the caller's original input order.
 */
function outcomeHuesForMarketHash(
  outcomes: ReadonlyArray<{ title: string; index: number }>,
): readonly OutcomeHueName[] {
  const sorted = [...outcomes].sort((a, b) => a.index - b.index);
  const used = new Set<OutcomeHueName>();
  const assigned = new Map<number, OutcomeHueName>();

  for (const o of sorted) {
    const normalized = o.title.trim().toLowerCase();
    if (normalized === 'yes') {
      used.add('green');
      assigned.set(o.index, 'green');
    } else if (normalized === 'no') {
      used.add('red');
      assigned.set(o.index, 'red');
    }
  }

  for (const o of sorted) {
    if (assigned.has(o.index)) continue;
    let hue = outcomeHueFromTitleHash(o.title);
    if (used.has(hue)) {
      const startIdx = OUTCOME_HUES.indexOf(hue);
      let found = false;
      for (let step = 1; step < OUTCOME_HUES.length; step++) {
        const candidate = OUTCOME_HUES[(startIdx + step) % OUTCOME_HUES.length];
        if (candidate !== undefined && !used.has(candidate)) {
          hue = candidate;
          found = true;
          break;
        }
      }
      if (!found) {
        throw new Error(
          `outcomeHuesForMarket: collision-walk exhausted for outcome index ${String(o.index)}`,
        );
      }
    }
    used.add(hue);
    assigned.set(o.index, hue);
  }

  return outcomes.map((o) => {
    const hue = assigned.get(o.index);
    if (hue === undefined) {
      throw new Error(
        `outcomeHuesForMarket: missing assignment for outcome index ${String(o.index)}`,
      );
    }
    return hue;
  });
}

/**
 * Precise-order per-market hue assignment. Yes/No-derivative labels reserve
 * green/red (each anchor claimed at most once); every other outcome takes its
 * canonical-index pick from the market palette, selecting the first free hue on
 * collision so per-market hues stay unique for K ≤ 16. Returns hues in the
 * caller's original input order.
 */
function outcomeHuesForMarketPreciseOrder(
  outcomes: ReadonlyArray<{ title: string; index: number }>,
  marketPubkey?: string,
): readonly OutcomeHueName[] {
  const palette = preciseOrderPalette(marketPubkey);
  const sorted = [...outcomes].sort((a, b) => a.index - b.index);
  const used = new Set<OutcomeHueName>();
  const assigned = new Map<number, OutcomeHueName>();

  let greenClaimed = false;
  let redClaimed = false;
  for (const o of sorted) {
    if (!greenClaimed && isYesOutcomeTitle(o.title)) {
      used.add('green');
      assigned.set(o.index, 'green');
      greenClaimed = true;
    } else if (!redClaimed && isNoOutcomeTitle(o.title)) {
      used.add('red');
      assigned.set(o.index, 'red');
      redClaimed = true;
    }
  }

  for (const o of sorted) {
    if (assigned.has(o.index)) continue;
    const preferred = palette[o.index];
    const hue =
      preferred !== undefined && !used.has(preferred)
        ? preferred
        : palette.find((h) => !used.has(h));
    if (hue === undefined) {
      throw new Error(
        `outcomeHuesForMarket: precise-order ran out of hues for outcome index ${String(o.index)}`,
      );
    }
    used.add(hue);
    assigned.set(o.index, hue);
  }

  return outcomes.map((o) => {
    const hue = assigned.get(o.index);
    if (hue === undefined) {
      throw new Error(
        `outcomeHuesForMarket: missing assignment for outcome index ${String(o.index)}`,
      );
    }
    return hue;
  });
}

/**
 * Per-market hue assignment with mode dispatch. Returns one `OutcomeHueName` per
 * input outcome, in the caller's original input order. Distinct indexes get
 * distinct hues for up to 16 outcomes; a repeated index (the same outcome held
 * in several wallets, or a cross-market rollup) resolves to that outcome's
 * single hue — never a throw.
 *
 * - Empty input → empty output.
 * - > 16 DISTINCT indexes → throw (only 16 hues exist).
 */
export function outcomeHuesForMarket(
  outcomes: ReadonlyArray<{ title: string; index: number }>,
  mode: OutcomeColorMode = 'precise-order',
  marketPubkey?: string,
): readonly OutcomeHueName[] {
  if (outcomes.length === 0) return [];
  // A caller may pass the same `index` more than once — one outcome held across
  // several wallets, or a cross-market position rollup. Both resolvers key
  // assignments by `index`, so duplicates collapse to a single hue: same
  // outcome, same colour. That is the correct result and must never throw — a
  // hue helper on the render path taking down the whole page is a far worse
  // failure than any colour. Capacity is therefore bounded by the count of
  // DISTINCT indexes (only 16 hues exist), not the row count.
  const distinctIndexes = new Set(outcomes.map((o) => o.index)).size;
  if (distinctIndexes > OUTCOME_HUES.length) {
    throw new Error(
      `outcomeHuesForMarket: cannot assign unique hues to ${String(distinctIndexes)} outcomes (max ${String(OUTCOME_HUES.length)})`,
    );
  }
  if (mode === 'precise-order') {
    return outcomeHuesForMarketPreciseOrder(outcomes, marketPubkey);
  }
  return outcomeHuesForMarketHash(outcomes);
}

function preciseOrderPalette(marketPubkey?: string): readonly OutcomeHueName[] {
  return marketPubkey === undefined
    ? PRECISE_ORDER_HUES
    : marketOutcomePalette(fnv1a(marketPubkey));
}

/**
 * Bridge helper for chart libraries that consume CSS color strings. Returns a
 * `var(--color-outcome-{hue}-{role})` reference — the cascade resolves the
 * actual color at paint time, so it works in light + dark automatically.
 * (Web-only in practice; harmless string builder shared here.)
 */
export function outcomeHueToVar(hue: OutcomeHueName, role: OutcomeRole): string {
  return `var(--color-outcome-${hue}-${role})`;
}

/**
 * Bridge helper for chart lines that should use a fixed palette step (e.g. 400)
 * rather than the mode-dependent semantic `text` token. Maps to
 * `var(--outcome-{hue}-{step})`.
 */
export function outcomeHueToStepVar(hue: OutcomeHueName, step: OutcomePaletteStep): string {
  return `var(--outcome-${hue}-${String(step)})`;
}
